import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core'
import { SuspectMap } from '../types/SuspectMap';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from 'src/environments/environment';
import * as wkt from 'wkt';
import * as geojson from "geojson";
import * as bbox from "geojson-bbox";
import { SharedService } from 'src/app/services/SharedService';
import { LocationSnapshotFilters } from '../types/LocationSnapshotFilters';
import { DxPivotGridComponent } from 'devextreme-angular';
import { Workbook } from 'exceljs';



@Component({
  selector: 'suspect-map',
  templateUrl: './suspect-map.component.html',
  styleUrls: ['./suspect-map.component.scss']
})
export class SuspectMapComponent implements OnInit, AfterViewInit, OnChanges {

  @Input()
  suspectMap: SuspectMap;

  @Input()
  filters: LocationSnapshotFilters;

  @Output() export = new EventEmitter();

  loader: Loader;
  google: any;
  map: google.maps.Map;
  bounds: any;

  showInfoText = false;
  hideLegend = true;

  header1_line1: string = '';
  header1_line2: string = '';  
  header2: string = '';
  header3_line1: string = '';
  header3_line2: string = '';
  header4: string = '';

  all_headers: any;

  indSegments: Set<string> = new Set();

  //Using only these 8 colors which are able to give a clear color variation when plotted together as small markers. 
  //When more colors are added, they only conflict with each other, and user will not be able to differentiate between them.
  //So we will use a combination of colors and shapes (circles/diamonds) to produce better variations.
  indSegColorBands = ['#f05a29','#4cc9f0',
                      '#bd4ef9','#deab90',
                      '#048ba8','#ffba08',
                      '#ea698b','#ccff33'];

  @ViewChild('SuspectsMapCustNearPlantGrid', {static : false}) suspectsMapCustNearPlantGrid : DxPivotGridComponent;
  pivotGrid_CustomersNearPlant_Datasource: any;

  constructor(private sharedSvc: SharedService) {

  }


  ngOnInit(): void {

  }

  
  ngAfterViewInit(): void {

    //This loads the google javascript API once.
    new Loader({
      apiKey: environment.gmaps,
      version: 'weekly'
    }).load().then((google) => {
      this.google = google;
    });
    
  }

  //The 'main' component of Location Snapshot will pass @Input data to its child component (this component)
  //This method could be called multiple times, whenever there is some change to the @Input data.
  //By design, not always all data will be received at once. So in this method, we check if required data is received,
  //and then call methods one by one to paint the page.
  ngOnChanges(changes: SimpleChanges): void {    

    //Set headers if required data is received.
    if(this.filters.siteNames && this.filters.globalSegments && this.filters.radiusAroundEachPlant){
      this.setHeaders();
    }

    let mapViewport = document.getElementById("suspects-map-viewport");

    //The application-data contains 5 main data arrays. 
    //Paint the map and populate datagrid if there is data in any of these arrays.
    if(this.suspectMap.customersNearPlantData.length > 0
      || this.suspectMap.apPlantsData.length > 0
      || this.suspectMap.competitorPlantsData.length > 0
      || this.suspectMap.competitorAPPlantsData.length > 0
      || this.suspectMap.proposedPlantsData.length > 0){
      this.showInfoText = false;
      if(mapViewport) mapViewport.innerHTML = "";
      this.bounds = null;
      this.initMap();
      this.setCustomersNearPlant_Datasource();
    }else{ //If main data arrays are empty, just show some info inside map viewport.
      if(mapViewport) mapViewport.innerHTML = "<div>No data available. Map cannot be generated.</div>";
      this.showInfoText = true;
    }
    
  }

  setHeaders(){
    this.header1_line1 = `Prospects Map for Industry Segments: ${this.filters.globalSegments}`;
    this.header1_line2 = `Within ${this.filters.radiusAroundEachPlant} miles of ${this.filters.siteNames}`;    

    this.header2 = "AIR PRODUCTS CONFIDENTIAL - INTERNAL USE ONLY";

    this.header3_line1 = 'Isoline/circles are only used for visual guidance and not used for or representative of any calculations in the reports.';
    this.header3_line2 = 'Green squares are AP plants.  Black dots are competitor plants.  Green diamonds are AP proposed plants. You may mouse-over any of these to get details.';

    this.header4 = 'Summary By Global Industry Segment';

    this.all_headers = {
      header1_line1: this.header1_line1,
      header1_line2: this.header1_line2,      
      header2: this.header2,
      header3_line1: this.header3_line1,
      header3_line2: this.header3_line2,
      header4: this.header4
    };

  }


  initMap() {      
    this.generateMap();
  }

  async generateMap() {

    this.map = new this.google.maps.Map(document.getElementById("suspects-map-viewport") as HTMLElement, {
      zoom: 6,
      mapTypeId: "terrain",
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,      
      disableDoubleClickZoom: true,
      mapId: environment.mapId
    });
        

    //We use async/await on some methods, so that they finish plotting their markers before we move to next.
    //This will control which markers appear above and be visible. Markers plotted last will appear above and be visible.
    //We plot the markers in below order
    //  1. Customer Sites (colored circles)
    //  2. Competitor Sites (black circles)
    //  3. Competitor AP Plant sites (Green Square - small)
    //  4. AP Plant Sites (Green Square - large)    
    //  5. Proposed Plant Sites (Green Diamond) (if power user)

    //First plot the customer sites
    this.indSegments = await this.plotCustomerSites(this.map);

    this.prepareLegend(Array.from(this.indSegments));

    await this.plotCompetitorSites(this.map);

    await this.plotAPPlantSites(this.map);

    await this.plotCompetitorAPSites(this.map);

    await this.plotBullsEye(this.map);

    if(this.sharedSvc.UserRoles.isIGPower){
      await this.plotProposedPlantSites(this.map);
    }

    this.setFeatureStyles(this.map);

  }

  async plotCustomerSites(map: google.maps.Map){

    await this.generateGlobalIndSegColors();

    let uniqueIndSegments = new Set<string>();

    this.suspectMap.customersNearPlantData.forEach(customer => {

      let point = wkt.parse(customer.geoLocation);

      let imageid = this.getGlobalIndSegImgId(customer.globalIndustrySegment);
      
      let marker = new google.maps.Marker({
        icon: `assets/img/mapmarkers/${imageid}.png`,
        position: { lat: point.coordinates[1], lng: point.coordinates[0] },          
        map: map,     
        zIndex: -99
      });      

      uniqueIndSegments.add(customer.globalIndustrySegment);
    });

    return uniqueIndSegments;
  }

  prepareLegend(segList: string[]){

    this.hideLegend = true;

    let mapLegend = document.getElementById("suspects-map-legend") as HTMLElement; 

    //Clear any previous legend items
    let legendItems = Array.from(mapLegend.getElementsByClassName("suspects-map-legend-item"));
    legendItems.forEach(item => item.innerHTML = "");

    if(segList.length > 0){
      segList.forEach(segName => {
        const div = document.createElement("div");
        div.className = "suspects-map-legend-item";
        div.innerHTML = `<span>${this.getGlobalIndSegSVG(segName)}</span><span style="padding-right: 15px;"></span>${segName}`;
        mapLegend.appendChild(div);
      });

    }else{
      const div = document.createElement("div");
      div.innerHTML = "<p>No data available for the given search criteria.</p>"          
      mapLegend.appendChild(div);
    }

    this.hideLegend = false;

  }

  async plotAPPlantSites(map: google.maps.Map){

    const { AdvancedMarkerElement } = await this.google.maps.importLibrary("marker");

    const parser = new DOMParser();

    let color = (this.filters.plantCompany == 'Air Products')? 'lime': 'red';

    let apPlantSVGString = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15">
                              <rect width="15" height="15" style="fill:${color};stroke-width:2;stroke:rgb(0,0,0)" />
                            </svg>`;

    let miles125Array = [];

    this.suspectMap.apPlantsData.forEach(apPlant => {

      let point = wkt.parse(apPlant.geoLocation); 

      let marker = new AdvancedMarkerElement({
        map: map,
        position: { lat: point.coordinates[1], lng: point.coordinates[0] },
        content: parser.parseFromString(apPlantSVGString, 'image/svg+xml').documentElement,
        title: apPlant.plantCoSite
      });

      miles125Array.push({ type: 'miles125', geo: wkt.parse(apPlant.miles125) });

    });

    // Looks like creating a extra ring on Maps
    // let featureCollection = geojson.parse(miles125Array, {GeoJSON: 'geo'});

    // map.data.addGeoJson(featureCollection);

  }

  async plotProposedPlantSites(map: google.maps.Map){

    const { AdvancedMarkerElement } = await this.google.maps.importLibrary("marker");

    const parser = new DOMParser();

    let proposedPlantSVGString = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20">
                                    <rect width="10" height="10" x="10" y="-4" transform="rotate(45)" 
                                      style="fill:lime;stroke-width:1;stroke:rgb(0,0,0)" />
                                  </svg>`;

    this.suspectMap.proposedPlantsData.forEach(proposedPlant => {

      let point = wkt.parse(proposedPlant.geoLocation); 

      let marker = new AdvancedMarkerElement({
        map: map,
        position: { lat: point.coordinates[1], lng: point.coordinates[0] },
        content: parser.parseFromString(proposedPlantSVGString, 'image/svg+xml').documentElement,
        title: proposedPlant.plantCoSite
      });

    });

  }

  async plotCompetitorSites(map: google.maps.Map){

    const { AdvancedMarkerElement } = await this.google.maps.importLibrary("marker");

    const parser = new DOMParser();

    let compSVGString = '<svg xmlns="http://www.w3.org/2000/svg" height="10" width="10">  <circle cx="5" cy="5" r="4" stroke="black" stroke-width="0.5" fill="black" /> </svg>';    


    this.suspectMap.competitorPlantsData.forEach(compPlant => {

      let point = wkt.parse(compPlant.geoLocation); 
            
      let marker = new AdvancedMarkerElement({
        map: map,
        position: { lat: point.coordinates[1], lng: point.coordinates[0] },
        content: parser.parseFromString(compSVGString, 'image/svg+xml').documentElement,
        title: compPlant.plantCoSite
      });    

    });

  }

  async plotCompetitorAPSites(map: google.maps.Map){

    const { AdvancedMarkerElement } = await this.google.maps.importLibrary("marker");

    const parser = new DOMParser();    
    
    let compAP_SVGString = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15">
                              <rect width="9" height="9" x="3" y="3" 
                                style="fill:lime;stroke-width:1;stroke:rgb(0,0,0)" />
                            </svg>`;

    this.suspectMap.competitorAPPlantsData.forEach(compAPPlant => {

      let point = wkt.parse(compAPPlant.geoLocation); 
            
      let marker = new AdvancedMarkerElement({
        map: map,
        position: { lat: point.coordinates[1], lng: point.coordinates[0] },
        content: parser.parseFromString(compAP_SVGString, 'image/svg+xml').documentElement,
        title: compAPPlant.plantCoSite
      });
            
    });

  }

  async plotBullsEye(map: google.maps.Map){

    //Add rings
    let ringArray = [];
    this.suspectMap.bullsEyeData.forEach(item => {
      ringArray.push({ type: 'bullsEye', geo: wkt.parse(item.bullseye) });      
    });

    let featureCollection = geojson.parse(ringArray, {GeoJSON: 'geo'});

    if(!this.bounds){
      let boundingBox = bbox(featureCollection);
      this.bounds = new google.maps.LatLngBounds();      
      this.bounds.extend({lat: boundingBox[1], lng: boundingBox[0]});
      this.bounds.extend({lat: boundingBox[3], lng: boundingBox[2]});
    }

    this.adjustSuspectsMapViewport();

    map.data.addGeoJson(featureCollection);

    //Add distance labels to the rings
    const { AdvancedMarkerElement } = await this.google.maps.importLibrary("marker");
    const parser = new DOMParser();

    this.suspectMap.bullsEyeData.forEach(item => {

      let point = wkt.parse(item.geoPoint);

      let svgText = `<svg xmlns="http://www.w3.org/2000/svg" height="30" width="60">
                        <text x="5" y="28" font-size="12" 
                          font-weight="bold" fill="black">${item.dist} miles</text>
                      </svg>`;

      let marker = new AdvancedMarkerElement({
        map: map,
        position: { lat: point.coordinates[1], lng: point.coordinates[0] },
        content: parser.parseFromString(svgText, 'image/svg+xml').documentElement,
      });

    });

  }

  adjustSuspectsMapViewport(){
    this.map.fitBounds(this.bounds);
    this.map.setCenter(this.bounds.getCenter());
  }

  setFeatureStyles(map: google.maps.Map) {

    let bullsEyeStyle = {
      strokeColor: "#4d4d4d",
      strokeOpacity: 1.0,
      strokeWeight: 2,        
      fillOpacity: 0.0,
      zIndex: 99999   
    };

    let miles125Style = {
      strokeColor: "#4d4d4d",
      strokeOpacity: 1.0,
      strokeWeight: 2,
      fillOpacity: 0.0,
      zIndex: 99999
    };

    map.data.setStyle(function(feature) {    
      
      let type = feature.getProperty('type');

      if(type == 'bullsEye') return bullsEyeStyle;

      if(type == 'miles125') return miles125Style;
      
    });

  }

  generateGlobalIndSegColors(){

    let segmentsCount = this.suspectMap.globalIndSegments.length;

    //We have 8 color codes. Repeat them, so that same colors will be used with different shapes below
    let colors = [...this.indSegColorBands, ...this.indSegColorBands];

    this.suspectMap.globalIndSegments 
      = this.suspectMap.globalIndSegments
          .map((segment,i) => {

            if(i < 8){  //First 8 segments will be denoted by colored circles
              segment.segSVGString = `<svg xmlns="http://www.w3.org/2000/svg" height="10" width="10">
                                        <circle cx="5" cy="5" r="4" stroke="black" stroke-width="0.5" fill="${colors[i]}" />
                                      </svg>`; 

              segment.imageid = `circle${i+1}`;
                                                    
            }else if(i >=8 && i < 16){ //next 8 segments will be denoted by colored triangles

              segment.segSVGString = `<svg height="12" width="12">
                                        <polygon points="6,2 11,11 1,11" style="fill:${colors[i]};
                                          stroke:black;stroke-width:0.5" />
                                      </svg>`;
              segment.imageid = `triangle${i+1}`;
                  
            } // if there are more segments in future, use another shape other than a circle or diamond
            
            return segment;
          }); 

  }

  getGlobalIndSegSVG(segName: string){
    return this.suspectMap.globalIndSegments.find(seg => seg.name == segName)?.segSVGString;
  }

  getGlobalIndSegImgId(segName: string){
    return this.suspectMap.globalIndSegments.find(seg => seg.name == segName)?.imageid;
  }

  //Pivot Grid
  setCustomersNearPlant_Datasource(){

    this.suspectMap.customersNearPlantData.forEach(item => {
      item.x = 'Prospects Summary';
      return item;
    })

    this.pivotGrid_CustomersNearPlant_Datasource = {
      fields: [{
        caption: 'Global Industry Segment',
        width: 200,
        dataField: 'globalIndustrySegment',
        expanded: true,        
        area: 'row',
      }, {
        caption: 'Prospects Summary',
        width: 120,
        dataField: 'x',
        area: 'column',         
      }, 
      {
        caption: 'Count',
        dataField: 'globalIndustrySegment',
        dataType: 'string',
        summaryType: 'count',
        area: 'data',
        format: { type: 'fixedPoint', precision: 0 },
      },{
        caption: 'Predicted MTD',        
        dataField: 'mtpd',
        dataType: 'number',
        summaryType: 'sum',
        area: 'data',
        format: { type: 'fixedPoint', precision: 0 }
      },{
        caption: 'MTD, No AP',        
        dataField: 'mtpDnoAP',
        dataType: 'number',
        summaryType: 'sum',
        area: 'data',
        format: { type: 'fixedPoint', precision: 0 }
      }],
      store: this.suspectMap.customersNearPlantData
    }
  }

  exportExcel($event){
    this.export.emit();
  }

}